import type { InventoryRow } from '../types/inventoryRow';

export const POC_INVENTORY_MANUAL_PIECE_EVENT = 'poc-inventory-manual-piece-changed';

const STORAGE_KEY = 'poc-app-inventory-manual-pieces-v1';

export interface SerializedManualInventoryLine {
  id: string;
  sku: string;
  referencePiece: string;
  referenceFournisseur: string;
  fournisseur: string;
  article: string;
  emplacement: string;
  quantiteTheorique: number;
  quantiteComptee: string;
  paHt: number;
  pvTtc: number;
  commentaire: string;
  /** Absent dans les données legacy : traité comme false. */
  comptageValide?: boolean;
  /** Validation superviseur (« Contrôlé »). */
  controleValide?: boolean;
  comptePar?: string;
  controlePar?: string;
}

interface StorageShape {
  linesByReference: Record<string, SerializedManualInventoryLine[]>;
}

const emptyStorage = (): StorageShape => ({ linesByReference: {} });

const getCountedQty = (quantiteComptee: string): number => {
  const trimmed = quantiteComptee.trim();

  if (trimmed === '') {
    return 0;
  }

  const n = parseInt(trimmed, 10);

  return Number.isFinite(n) && n >= 0 ? n : 0;
};

function readStorage(): StorageShape {
  if (typeof window === 'undefined') {
    return emptyStorage();
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return emptyStorage();
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!parsed || typeof parsed !== 'object') {
      return emptyStorage();
    }

    const obj = parsed as { linesByReference?: unknown };

    if (!obj.linesByReference || typeof obj.linesByReference !== 'object') {
      return emptyStorage();
    }

    return { linesByReference: obj.linesByReference as Record<string, SerializedManualInventoryLine[]> };
  } catch {
    return emptyStorage();
  }
}

function writeStorage(data: StorageShape): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function loadManualLinesForInventory(referenceInventaire: string): SerializedManualInventoryLine[] {
  const data = readStorage();

  return data.linesByReference[referenceInventaire] ?? [];
}

export function appendManualLineToInventory(
  referenceInventaire: string,
  line: SerializedManualInventoryLine,
): void {
  const data = readStorage();
  const prev = data.linesByReference[referenceInventaire] ?? [];

  data.linesByReference[referenceInventaire] = [...prev, line];
  writeStorage(data);
  window.dispatchEvent(new CustomEvent(POC_INVENTORY_MANUAL_PIECE_EVENT));
}

export function updateManualLineFields(
  referenceInventaire: string,
  lineId: string,
  patch: Partial<
    Pick<
      SerializedManualInventoryLine,
      | 'quantiteComptee'
      | 'commentaire'
      | 'comptageValide'
      | 'controleValide'
      | 'comptePar'
      | 'controlePar'
    >
  >,
): void {
  const data = readStorage();
  const list = data.linesByReference[referenceInventaire];

  if (!list) {
    return;
  }

  const index = list.findIndex((line) => line.id === lineId);

  if (index === -1) {
    return;
  }

  list[index] = { ...list[index], ...patch };
  data.linesByReference[referenceInventaire] = [...list];
  writeStorage(data);
  window.dispatchEvent(new CustomEvent(POC_INVENTORY_MANUAL_PIECE_EVENT));
}

export function isManualInventoryLineId(lineId: string): boolean {
  return lineId.includes('-manual-');
}

export function applyManualLinesToInventoryRow(row: InventoryRow): InventoryRow {
  const manual = loadManualLinesForInventory(row.reference);

  if (manual.length === 0) {
    return row;
  }

  const addArticles = manual.length;
  const addTheo = manual.reduce((acc, line) => acc + line.quantiteTheorique, 0);
  const addCounted = manual.reduce((acc, line) => acc + getCountedQty(line.quantiteComptee), 0);
  const addPa = manual.reduce((acc, line) => acc + line.paHt, 0);
  const addPv = manual.reduce((acc, line) => acc + line.pvTtc, 0);
  const nextTheo = row.quantiteTheorique + addTheo;
  const nextCounted = row.quantiteComptee + addCounted;

  return {
    ...row,
    nombreArticles: row.nombreArticles + addArticles,
    quantiteTheorique: nextTheo,
    quantiteComptee: nextCounted,
    sommePaHt: row.sommePaHt + addPa,
    sommePvTtc: row.sommePvTtc + addPv,
    ecart: nextCounted - nextTheo,
  };
}
